import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { updateBusinessList } from '../../../services/MasterApi';
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCreditCard, FaLock, FaCheckCircle } from 'react-icons/fa';

// ─────────────────────────────────────────────────────────────────────────────
// MockCheckoutForm — Fully functional payment form for testing.
// Calls backend /stripe/create-payment-intent (returns mock clientSecret).
// No real Stripe key required. Switch to CheckoutForm when you have real keys.
// ─────────────────────────────────────────────────────────────────────────────

export default function MockCheckoutForm({ total }) {
  const navigate = useNavigate();
  const business = useSelector((state) => state.business);
  const transactionId = localStorage.getItem("transactionId");
  const businessTypeId = business?.businessTypeId;
  const fullName = business?.primaryContact?.fullName;
  const email = business?.primaryContact?.email;

  const [form, setForm] = useState({
    cardholderName: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    postalCode: '',
  });
  const [cardBrand, setCardBrand] = useState('unknown');
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  // ── Card brand detection ──────────────────────────────────────────────────
  const detectBrand = (num) => {
    const n = num.replace(/\s/g, '');
    if (/^4/.test(n)) return 'visa';
    if (/^5[1-5]/.test(n)) return 'mastercard';
    if (/^3[47]/.test(n)) return 'amex';
    if (/^6(?:011|5)/.test(n)) return 'discover';
    return 'unknown';
  };

  const getCardIcon = () => {
    const props = { style: { fontSize: 28, marginLeft: 8 } };
    switch (cardBrand) {
      case 'visa':       return <FaCcVisa {...props} style={{ ...props.style, color: '#1a1f71' }} />;
      case 'mastercard': return <FaCcMastercard {...props} style={{ ...props.style, color: '#eb001b' }} />;
      case 'amex':       return <FaCcAmex {...props} style={{ ...props.style, color: '#2e77bc' }} />;
      case 'discover':   return <FaCcDiscover {...props} style={{ ...props.style, color: '#f76f20' }} />;
      default:           return <FaCreditCard {...props} style={{ ...props.style, color: '#adb5bd' }} />;
    }
  };

  // ── Input formatters ──────────────────────────────────────────────────────
  const formatCardNumber = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + ' / ' + digits.slice(2);
    return digits;
  };

  // ── Change handler ────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'cardNumber') {
      formatted = formatCardNumber(value);
      setCardBrand(detectBrand(formatted));
    }
    if (name === 'expiry') formatted = formatExpiry(value);
    if (name === 'cvc') formatted = value.replace(/\D/g, '').slice(0, 4);
    if (name === 'postalCode') formatted = value.replace(/[^a-zA-Z0-9 -]/g, '').slice(0, 10);

    setForm((prev) => ({ ...prev, [name]: formatted }));
    setErrors((prev) => ({ ...prev, [name]: null }));
    setGlobalError(null);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.cardholderName.trim()) errs.cardholderName = 'Cardholder name is required.';
    const rawCard = form.cardNumber.replace(/\s/g, '');
    if (rawCard.length < 13) errs.cardNumber = 'Enter a valid card number.';
    const expiryRaw = form.expiry.replace(/\s/g, '').replace('/', '');
    if (expiryRaw.length < 4) errs.expiry = 'Enter a valid expiry (MM/YY).';
    else {
      const month = parseInt(expiryRaw.slice(0, 2), 10);
      if (month < 1 || month > 12) errs.expiry = 'Invalid expiry month.';
    }
    if (form.cvc.length < 3) errs.cvc = 'Enter a valid CVC.';
    if (!/^[a-zA-Z0-9 -]{3,10}$/.test(form.postalCode.trim())) errs.postalCode = 'Enter a valid ZIP/postal code.';
    return errs;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setProcessing(true);
    setGlobalError(null);

    try {
      // MOCK MODE: Skip backend payment-intent call entirely.
      // Directly record the transaction and redirect to success.
      const mockChargeId = `ch_mock_${Date.now()}`;

      const paymentData = {
        businessTransactionId: parseInt(transactionId),
        businessTypeId: parseInt(businessTypeId),
        transactionStatusId: 3,
        primaryContact: { fullName, email },
        payment: {
          paymentModeId: 2,
          paymentStructureId: 2,
          paymentStatusId: 1,
          paymentTransactionId: mockChargeId,
          payer: {
            id: `usr_${Math.random().toString(36).substr(2, 9)}`,
            name: form.cardholderName,
            email: email || 'billing@oktobiz.com',
            countryId: 1,
            updateDate: Date.now(),
            createDate: Date.now(),
          },
          payee: {
            email: 'billing@oktobiz.com',
            merchantId: 'acct_stripe_oktobiz',
          },
        },
      };

      try {
        await updateBusinessList(paymentData);
      } catch (dbErr) {
        console.warn('DB update warning (non-fatal):', dbErr);
      }

      setShowAlert(true);
      setTimeout(() => navigate('/confirm'), 1200);

    } catch (err) {
      console.error('Mock payment error:', err);
      setGlobalError('An error occurred. Please try again.');
      setProcessing(false);
    }
  };


  // ── Field style helper ────────────────────────────────────────────────────
  const fieldStyle = (name) => ({
    width: '100%',
    padding: '11px 14px',
    border: `1.5px solid ${errors[name] ? '#e53935' : '#dadce0'}`,
    borderRadius: 6,
    fontSize: 15,
    color: '#202124',
    fontFamily: 'Roboto, sans-serif',
    background: '#fff',
    outline: 'none',
    letterSpacing: name === 'cardNumber' ? '0.08em' : 'normal',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  });

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }} noValidate>

      {/* Success alert */}
      {showAlert && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#e6f4ea', border: '1px solid #34a853', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
          <FaCheckCircle style={{ color: '#1e8e3e', fontSize: 18 }} />
          <span style={{ color: '#1e8e3e', fontFamily: 'Roboto, sans-serif', fontWeight: 500 }}>Payment successful! Redirecting…</span>
        </div>
      )}

      {/* Global error */}
      {globalError && (
        <div style={{ background: '#fce8e6', border: '1px solid #e53935', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#c62828', fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>
          {globalError}
        </div>
      )}

      {/* Cardholder Name */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 500, color: '#3c4043', marginBottom: 6 }}>
          Cardholder Name
        </label>
        <input
          name="cardholderName"
          type="text"
          placeholder="Name on card"
          value={form.cardholderName}
          onChange={handleChange}
          style={fieldStyle('cardholderName')}
          autoComplete="cc-name"
        />
        {errors.cardholderName && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4, fontFamily: 'Roboto, sans-serif' }}>{errors.cardholderName}</p>}
      </div>

      {/* Card Number */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 500, color: '#3c4043', marginBottom: 6 }}>
          Card Number
        </label>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            name="cardNumber"
            type="text"
            inputMode="numeric"
            placeholder="1234 5678 9012 3456"
            value={form.cardNumber}
            onChange={handleChange}
            style={{ ...fieldStyle('cardNumber'), paddingRight: 48 }}
            autoComplete="cc-number"
          />
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', lineHeight: 0 }}>
            {getCardIcon()}
          </span>
        </div>
        {errors.cardNumber && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4, fontFamily: 'Roboto, sans-serif' }}>{errors.cardNumber}</p>}
      </div>

      {/* Expiry + CVC */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 500, color: '#3c4043', marginBottom: 6 }}>
            Expiry Date
          </label>
          <input
            name="expiry"
            type="text"
            inputMode="numeric"
            placeholder="MM / YY"
            value={form.expiry}
            onChange={handleChange}
            style={fieldStyle('expiry')}
            autoComplete="cc-exp"
          />
          {errors.expiry && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4, fontFamily: 'Roboto, sans-serif' }}>{errors.expiry}</p>}
        </div>
        <div>
          <label style={{ display: 'block', fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 500, color: '#3c4043', marginBottom: 6 }}>
            CVC / CVV
          </label>
          <input
            name="cvc"
            type="text"
            inputMode="numeric"
            placeholder="123"
            value={form.cvc}
            onChange={handleChange}
            style={fieldStyle('cvc')}
            autoComplete="cc-csc"
          />
          {errors.cvc && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4, fontFamily: 'Roboto, sans-serif' }}>{errors.cvc}</p>}
        </div>
      </div>

      {/* Postal Code */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 500, color: '#3c4043', marginBottom: 6 }}>
          Billing ZIP / Postal Code
        </label>
        <input
          name="postalCode"
          type="text"
          inputMode="numeric"
          placeholder="10001"
          value={form.postalCode}
          onChange={handleChange}
          style={fieldStyle('postalCode')}
          autoComplete="postal-code"
        />
        {errors.postalCode && <p style={{ color: '#e53935', fontSize: 12, marginTop: 4, fontFamily: 'Roboto, sans-serif' }}>{errors.postalCode}</p>}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={processing || showAlert}
        style={{
          width: '100%',
          height: 48,
          background: processing || showAlert ? '#a8c7fa' : '#1a73e8',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 15,
          fontWeight: 500,
          fontFamily: 'Roboto, sans-serif',
          cursor: processing || showAlert ? 'not-allowed' : 'pointer',
          transition: 'background 0.15s',
          letterSpacing: '0.2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 14,
        }}
      >
        <FaLock style={{ fontSize: 13 }} />
        {processing ? 'Processing…' : showAlert ? 'Payment Complete ✓' : `Pay $${total?.toFixed(2) || '0.00'}`}
      </button>

      {/* Security note */}
      <p style={{ textAlign: 'center', fontFamily: 'Roboto, sans-serif', fontSize: 12, color: '#5f6368', margin: 0 }}>
        <FaLock style={{ fontSize: 11, marginRight: 4 }} />
        Test mode — no real charges will be made
      </p>

    </form>
  );
}
